import React, { useState } from 'react';
import { arrayOf, bool, func, node, number, oneOfType, object, string } from 'prop-types';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import compose from 'recompose/compose';
import clsx from 'clsx';

import {
  withStyles,
  Grid,
  Button,
  Tooltip,
  Typography,
  IconButton,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import CloseIcon from '@material-ui/icons/Close';

import {
  AlertMessages,
  Footer,
  Loading,
  Table,
  TablePagination,
  ErrorContainer,
} from '../components';
import styles from './list.styles';
import { GRID_ROWS_PER_PAGE_OPTIONS } from '../constants';
import { isFunction, uniq } from 'lodash';

const getRows = ({
  rows,
  expandedRows,
  setExpandedRows,
  selectedRows,
  setSelectedRows,
  selectedRowsObject,
  setSelectedRowsObject,
  disabledRows,
}) => (
  rows.map(
    row => ({
      ...row,
      expanded: expandedRows.includes(row.uuid),
      onClick: (uuid) => {
        if (expandedRows.includes(uuid)) {
          setExpandedRows(
            expandedRows.filter(e => (e !== uuid)),
          );
        } else {
          setExpandedRows(
            [...expandedRows, uuid],
          );
        }
      },
      selected: selectedRows.includes(row.uuid),
      disabled: disabledRows.includes(row.uuid),
      onSelectToggle: (e, row) => {
        if (e.target.checked) {
          setSelectedRows(
            [...selectedRows, row.uuid],
          );
          if(isFunction(setSelectedRowsObject)) {
            selectedRowsObject.push(row)
            setSelectedRowsObject(selectedRowsObject);
          }
        } else {
          setSelectedRows(
            selectedRows.filter(e => (e !== row.uuid)),
          );
          if(isFunction(setSelectedRowsObject)) {
            setSelectedRowsObject(selectedRowsObject.filter(e => (e !== row.uuid)));
          }
        }
      },
    }),
  )
);

export const List = (props) => {
  const {
    classes,
    userContext,
    listPageId,
    isLoading,
    notificationId,
    notificationStatus,
    setNotificationStatus,
    notificationMessage,
    setNotificationMessage,
    hasNotificationIcon = false,
    closeNotificationText,
    onCloseNotification,
    pageHeaderTitle,
    pageHeaderTooltipTitle,
    pageHeaderDescription,
    addButtonLabel,
    onAdd,
    disableAdd,
    filterAndSortContent,
    bulkActionsContent,
    columns,
    noResultsMessage,
    page,
    totalElements,
    totalPages,
    rowsPerPage: rowsPerPageParam,
    rowsPerPageOptions = GRID_ROWS_PER_PAGE_OPTIONS,
    onChangeRowsPerPage,
    onChangePage,
    onChangePreviousPage,
    onChangeNextPage,
    selectedRows = [],
    disabledRows = [],
    setSelectedRows,
    selectedRowsObject = [],
    setSelectedRowsObject,
    showListHeader = true,
    pageClass = null,
    pageBodyClass,
    pageFilterAndSortClass,
    uuidName = null,
    notifyMessages,
    hasPaginationDisabled = false,
    paginationTooltipMessage = '',
    hidePagination = false,
    hideFooter = false,
    ActionsMenu,
    errors = [],
    allowOrdering,
    onDragAndDrop,
    headerEndAdornment,
    headerEndAdornmentClass,
  } = props;
  let rowsPerPage;
  if (rowsPerPageOptions) {
    if (findIndex(rowsPerPageOptions, item => item === rowsPerPageParam) === -1) {
      rowsPerPage = rowsPerPageOptions[0];
    } else {
      rowsPerPage = rowsPerPageParam
    }
  }

  const [expandedRows, setExpandedRows] = useState([]);
  const rows = getRows({
    rows: props.rows,
    expandedRows,
    setExpandedRows,
    selectedRows,
    setSelectedRows,
    selectedRowsObject,
    setSelectedRowsObject,
    disabledRows,
  });

  const isSelectedAll = rows.every(item => item.selected);
  const isDisabledAll = rows.every(item => item.disabled);

  const onSelectAllRowsToggle = (e) => {
    const toggleRows = rows.map(row => row.uuid).filter(uuid => !disabledRows.includes(uuid));
    if(get(e, 'target.checked')) {
      setSelectedRows(uniq([...toggleRows, ...selectedRows]));
      if(isFunction(setSelectedRowsObject)) {
        setSelectedRowsObject(rows);
      }
    } else {
      setSelectedRows(
        selectedRows.filter(e => !toggleRows.includes(e)),
      );
      isFunction(setSelectedRowsObject) ? setSelectedRowsObject([]) : '';
    }
  }
  const onCloseButton = () => {
    setSelectedRows([]);
    isFunction(setSelectedRowsObject) ? setSelectedRowsObject([]) : '';
  }
  const notificationMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const headerEndAdornmentClassName = clsx(headerEndAdornmentClass, classes.headerEndAdornmentClass)

  return (
    <Grid
      className={clsx(pageClass, classes.page)}
      id={listPageId} data-apim-test={listPageId} data-layer7-test={listPageId}
    >
      {notificationMessage &&
        <AlertMessages
          className={classes.alertMessages}
          actionButtonClass={classes.alertMessagesCloseButton}
          id={notificationId}
          closeText={closeNotificationText}
          variant={notificationStatus}
          message={notificationMessage}
          onClose={() => {
            notificationMessages('', '');
            if (onCloseNotification) { onCloseNotification(); }
          }}
          hasNotificationIcon={hasNotificationIcon}
        />
      }
      {errors.length > 0 &&
        <div className={classes.errorContainer}>
          <ErrorContainer errors={errors} />
        </div>
      }
      {isLoading ?
        <Loading pageLoader />
        :
        <div className={clsx(pageBodyClass, classes.pageBody)}>
          {showListHeader &&
            <Grid container justify="space-between" className={pageHeaderTitle ? classes.pageHeader: classes.pageHeaderHidden}>
              <Grid item md={10} sm={9} xs={12}>
                <Grid container>
                  <Grid lg={12} className={classes.titleContainer}>
                    <Typography
                      variant="h1"
                      className={classes.pageTitle}
                      id={`${listPageId}-title`} data-apim-test={`${listPageId}-title`} data-layer7-test={`${listPageId}-title`}
                    >
                      {pageHeaderTitle}
                    </Typography>
                    {pageHeaderTooltipTitle &&
                      <Tooltip
                        arrow disableFocusListener disableTouchListener placement="top"
                        title={pageHeaderTooltipTitle}
                      >
                        <IconButton
                          size="small" edge="start" color="secondary"
                          className={classes.helpIcon}
                          id={`${listPageId}-help`} data-apim-test={`${listPageId}-help`} data-layer7-test={`${listPageId}-help`}
                          aria-label="gateway-bundle-help"
                        >
                          <HelpIcon />
                        </IconButton>
                      </Tooltip>
                    }
                    {headerEndAdornment &&
                      <Grid className={headerEndAdornmentClassName}>
                        {headerEndAdornment}
                      </Grid>
                    }
                  </Grid>
                  {pageHeaderDescription &&
                    <Grid lg={12} md={12} sm={12}>
                      <div
                        className={classes.pageHeaderDescription}
                        id={`${listPageId}-description`} data-apim-test={`${listPageId}-description`} data-layer7-test={`${listPageId}-description`}
                      >
                        {pageHeaderDescription}
                      </div>
                    </Grid>
                  }
                </Grid>
              </Grid>
              <Grid
                item md={2} sm={3} xs={12}
                className={classes.addButton}
              >
                {onAdd &&
                  <Button
                    variant="contained" color="secondary"
                    onClick={onAdd || (() => {})}
                    id={`${listPageId}-add`} data-apim-test={`${listPageId}-add`} data-layer7-test={`${listPageId}-add`}
                    disabled={disableAdd}
                  >
                    {addButtonLabel}
                  </Button>
                }
                {ActionsMenu && <ActionsMenu />}
              </Grid>
            </Grid>
          }

          {(selectedRows.length > 0 && bulkActionsContent) ?
            <Grid
              container justify="space-between"
              className={
                clsx(pageFilterAndSortClass, classes.pageFilterAndSort, classes.pageBulkActions)
              }
            >
              <Grid item md={11} sm={11} xs={11}>
                <Grid
                  container
                  id="bulk-actions" data-apim-test="bulk-actions" data-layer7-test="bulk-actions"
                >
                  {bulkActionsContent}
                </Grid>
              </Grid>
              <Grid item md={1} sm={1} xs={1}>
                <IconButton
                  size="small" edge="start" color="secondary"
                  className={classes.pageBulkActionsClose}
                  id={`${listPageId}-bulk-actions-close`} data-apim-test={`${listPageId}-bulk-actions-close`} data-layer7-test={`${listPageId}-bulk-actions-close`}
                  onClick={onCloseButton}
                >
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
            :
            <Grid
              container justify="space-between"
              className={clsx(pageFilterAndSortClass, classes.pageFilterAndSort)}
            >
              {filterAndSortContent}
            </Grid>
          }
          <Table
            userContext={userContext}
            id={`${listPageId}-list`}
            columns={columns}
            rows={rows}
            noResultsMessage={noResultsMessage}
            uuidName={uuidName}
            notifyMessages={notifyMessages}
            hasBulkActions={!!bulkActionsContent}
            selectedRows={selectedRows}
            onSelectAllRowsToggle={onSelectAllRowsToggle}
            isSelectedAll={isSelectedAll}
            isDisabledAll={isDisabledAll}
            allowOrdering={allowOrdering}
            onDragAndDrop={onDragAndDrop}
          />
          {!hidePagination &&
            <TablePagination
              id={`${listPageId}-pagination`}
              page={page}
              count={totalElements}
              pageCount={totalPages}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={rowsPerPageOptions}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onChangePage={onChangePage}
              onChangePreviousPage={onChangePreviousPage}
              onChangeNextPage={onChangeNextPage}
              hasPaginationDisabled={hasPaginationDisabled}
              paginationTooltipMessage={paginationTooltipMessage}
            />
          }
        </div>
      }
      {!hideFooter &&
        <Footer />
      }
    </Grid>
  );
};

List.propTypes = {
  addButtonLabel: string,
  bulkActionsContent: oneOfType([bool, object]),
  classes: object,
  closeNotificationText: string,
  columns: arrayOf(object),
  disableAdd: bool,
  disabledRows: arrayOf(string),
  filterAndSortContent: object,
  hasPaginationDisabled: bool,
  hideFooter: bool,
  hidePagination: bool,
  isLoading: bool,
  listPageId: string,
  noResultsMessage: string,
  notificationId: string,
  notificationMessage: string,
  notificationStatus: string,
  hasNotificationIcon : bool,
  notifyMessages: func,
  onAdd: oneOfType([func, bool]),
  onChangeNextPage: func,
  onChangePage: func,
  onChangePreviousPage: func,
  onChangeRowsPerPage: func,
  onCloseNotification: func,
  page: number,
  pageBodyClass: string,
  pageClass: string,
  pageFilterAndSortClass: string,
  pageHeaderDescription: node,
  pageHeaderTitle: string,
  pageHeaderTooltipTitle: string,
  paginationTooltipMessage: string,
  tagsContent: object,
  rows: arrayOf(object),
  rowsPerPage: number,
  rowsPerPageOptions: arrayOf(number),
  selectedRows: arrayOf(string),
  selectedRowsObject: arrayOf(object),
  setNotificationMessage: func,
  setNotificationStatus: func,
  setSelectedRows: func,
  setSelectedRowsObject: func,
  showListHeader: bool,
  totalElements: number,
  totalPages: number,
  userContext: object,
  uuidName: string,
  ActionsMenu:  func,
  errors: arrayOf(object),
  allowOrdering: bool,
  onDragAndDrop: func,
  headerEndAdornment: object,
  headerEndAdornmentClass: string,

};

export default compose(
  withStyles(styles),
)(List);